import React, { useState } from 'react';
import { Menu, X, Shield, Target, BarChart3, Settings, Home, Info, Lock } from 'lucide-react';
import { Button } from '../ui/Button';

interface NavigationMenuProps {
  isAuthenticated: boolean;
  currentView?: 'assessment' | 'dashboard' | 'about' | 'security';
  onNavigate?: (view: 'assessment' | 'dashboard' | 'about' | 'security') => void;
  onSignOut?: () => void;
  user?: {
    email: string;
  };
}

export const NavigationMenu: React.FC<NavigationMenuProps> = ({
  isAuthenticated,
  currentView = 'assessment',
  onNavigate,
  onSignOut,
  user
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const publicMenuItems = [
    { id: 'about', label: 'About', icon: Info, description: 'Learn about Red Set' },
    { id: 'security', label: 'Security', icon: Lock, description: 'Security & Compliance' }
  ];

  const authenticatedMenuItems = [
    { id: 'assessment', label: 'Assessment', icon: Target, description: 'Run adversarial audits' },
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3, description: 'View history & trends' }
  ];

  const handleNavigation = (view: 'assessment' | 'dashboard' | 'about' | 'security') => {
    onNavigate?.(view);
    setIsOpen(false);
  };

  return (
    <>
      {/* Mobile Menu Button */}
      <div className="md:hidden">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center justify-center p-2 rounded-lg bg-black/40 border border-red-500/30 text-white hover:bg-red-600/20 transition-colors"
          aria-label="Toggle menu"
        >
          {isOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {/* Desktop Navigation */}
      <nav className="hidden md:flex items-center space-x-4">
        {isAuthenticated ? (
          <>
            {authenticatedMenuItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => handleNavigation(item.id as any)}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    currentView === item.id
                      ? 'bg-red-600 text-white'
                      : 'bg-black/40 hover:bg-red-600/20 text-white border border-red-500/30'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span>{item.label}</span>
                </button>
              );
            })}
            <div className="h-4 w-px bg-red-500/30 mx-2" />
            <div className="flex items-center space-x-2 bg-black/40 px-3 py-2 rounded-lg border border-red-500/30">
              <Shield className="h-4 w-4 text-red-400" />
              <span className="text-sm text-white">{user?.email}</span>
            </div>
            <Button
              onClick={onSignOut}
              variant="danger"
              size="sm"
            >
              Sign Out
            </Button>
          </>
        ) : (
          <>
            {publicMenuItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => handleNavigation(item.id as any)}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    currentView === item.id
                      ? 'bg-red-600 text-white'
                      : 'bg-black/40 hover:bg-red-600/20 text-white border border-red-500/30'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </>
        )}
      </nav>

      {/* Mobile Menu Overlay */}
      {isOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="fixed inset-0 bg-black/50" onClick={() => setIsOpen(false)} />
          <div className="fixed top-0 right-0 h-full w-80 bg-gradient-to-b from-gray-900 to-black border-l border-red-500/30 shadow-xl">
            <div className="p-6">
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-xl font-bold text-white">Navigation</h2>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 rounded-lg bg-black/40 border border-red-500/30 text-white hover:bg-red-600/20"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-4">
                {isAuthenticated ? (
                  <>
                    <div className="mb-6">
                      <div className="flex items-center space-x-3 bg-black/40 p-3 rounded-lg border border-red-500/30">
                        <Shield className="h-5 w-5 text-red-400" />
                        <div>
                          <p className="text-sm font-medium text-white">{user?.email}</p>
                          <p className="text-xs text-red-400">Security Researcher</p>
                        </div>
                      </div>
                    </div>

                    {authenticatedMenuItems.map((item) => {
                      const Icon = item.icon;
                      return (
                        <button
                          key={item.id}
                          onClick={() => handleNavigation(item.id as any)}
                          className={`w-full flex items-center space-x-3 p-3 rounded-lg text-left transition-colors ${
                            currentView === item.id
                              ? 'bg-red-600 text-white'
                              : 'bg-black/40 hover:bg-red-600/20 text-white border border-red-500/30'
                          }`}
                        >
                          <Icon className="h-5 w-5" />
                          <div>
                            <p className="font-medium">{item.label}</p>
                            <p className="text-xs opacity-75">{item.description}</p>
                          </div>
                        </button>
                      );
                    })}

                    <div className="pt-4 border-t border-red-500/30">
                      <Button
                        onClick={onSignOut}
                        variant="danger"
                        className="w-full"
                      >
                        Sign Out
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    {publicMenuItems.map((item) => {
                      const Icon = item.icon;
                      return (
                        <button
                          key={item.id}
                          onClick={() => handleNavigation(item.id as any)}
                          className={`w-full flex items-center space-x-3 p-3 rounded-lg text-left transition-colors ${
                            currentView === item.id
                              ? 'bg-red-600 text-white'
                              : 'bg-black/40 hover:bg-red-600/20 text-white border border-red-500/30'
                          }`}
                        >
                          <Icon className="h-5 w-5" />
                          <div>
                            <p className="font-medium">{item.label}</p>
                            <p className="text-xs opacity-75">{item.description}</p>
                          </div>
                        </button>
                      );
                    })}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};